import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  Search,
  RefreshCw,
  TrendingUp,
  EyeOff,
  RotateCcw,
  Plus,
  Package,
  AlertTriangle,
  CheckCircle2,
  X,
} from "lucide-react";

const STATUS_STYLE = {
  open: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  queued: "bg-blue-50 text-blue-700 border border-blue-200",
  ignored: "bg-gray-100 text-gray-500 border border-gray-200",
};

export default function SaleRequirementsView({ shops = [], onQueueChange }) {
  const { user } = useAuth();
  const canBulkIgnore = user?.role === "master" || user?.role === "super_user";
  
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState([]);
  const [shopFilter, setShopFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("open");
  const [search, setSearch] = useState("");
  const [queueItem, setQueueItem] = useState(null); // {req: row} for AddToQueueDialog
  const [selectedIds, setSelectedIds] = useState(new Set());

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (shopFilter !== "all") params.shop_id = shopFilter;
      if (statusFilter !== "open") params.status = statusFilter;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get("/management/sale-requirements", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [shopFilter, statusFilter, search]);

  useEffect(() => {
    const t = setTimeout(fetchData, 250);
    return () => clearTimeout(t);
  }, [fetchData]);

  // Auto-refresh every 30 seconds to catch new sales made elsewhere
  useEffect(() => {
    const interval = setInterval(() => {
      fetchData();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleIgnore = async (id) => {
    if (!window.confirm("Hide this item from the requirements list?")) return;
    try {
      await api.post(`/management/sale-requirements/${id}/ignore`);
      toast.success("Ignored");
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleBulkIgnore = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Ignore ${selectedIds.size} selected item(s)? They will be hidden from the list but kept for 30 days.`)) return;
    try {
      const { data } = await api.post("/management/sale-requirements/bulk-ignore", {
        ids: Array.from(selectedIds),
      });
      toast.success(`Ignored ${data.ignored}${data.skipped?.length ? ` · skipped ${data.skipped.length}` : ""}`);
      setSelectedIds(new Set());
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    // Only select items that can be ignored (open status)
    const selectableItems = items.filter(r => r.status === "open");
    if (selectedIds.size === selectableItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(selectableItems.map(r => r.id)));
    }
  };

  // Clear selection when filters change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [shopFilter, statusFilter, search]);

  const handleReopen = async (id) => {
    try {
      await api.post(`/management/sale-requirements/${id}/reopen`);
      toast.success("Reopened");
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const handleAddToQueue = async (id, payload, strategy = "ask") => {
    try {
      await api.post(`/management/sale-requirements/${id}/add-to-queue`, {
        ...payload,
        duplicate_strategy: strategy,
      });
      toast.success("Added to restock queue");
      setQueueItem(null);
      fetchData();
      if (typeof onQueueChange === "function") onQueueChange();
    } catch (e) {
      // duplicate? backend returns 409 from create_manual_request
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409 && detail?.code === "duplicate_request") {
        if (
          window.confirm(
            `A pending restock request already exists for this product. Merge?` +
              `\n\nExisting: ${detail.existing.quantity_requested} units` +
              `\nNew: ${payload.quantity} units` +
              `\n\nOK = Merge · Cancel = Create separate`
          )
        ) {
          handleAddToQueue(id, payload, "merge");
        } else {
          handleAddToQueue(id, payload, "separate");
        }
        return;
      }
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-3" data-testid="sale-requirements-view">
      {/* Filters */}
      <Card className="p-3">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
          <div className="relative sm:col-span-2">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              className="pl-8 h-9"
              placeholder="Search by product, brand, barcode..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="sr-search-input"
            />
          </div>
          <Select value={shopFilter} onValueChange={setShopFilter}>
            <SelectTrigger className="h-9" data-testid="sr-shop-filter">
              <SelectValue placeholder="All shops" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All shops</SelectItem>
              {shops.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="flex items-center gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="h-9 flex-1" data-testid="sr-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="open">Open</SelectItem>
                <SelectItem value="queued">Queued</SelectItem>
                <SelectItem value="ignored">Ignored</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchData}
              className="h-9 w-9 p-0"
              data-testid="sr-refresh-btn"
              title="Refresh"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </Button>
          </div>
        </div>
        
        {/* Bulk actions - only for master/super_user */}
        {canBulkIgnore && selectedIds.size > 0 && (
          <div className="mt-3 pt-3 border-t flex items-center gap-3">
            <span className="text-sm text-muted-foreground">{selectedIds.size} selected</span>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs text-amber-700 border-amber-300 hover:bg-amber-50"
              onClick={handleBulkIgnore}
              data-testid="sr-bulk-ignore-btn"
            >
              <X className="w-3.5 h-3.5 mr-1" /> Ignore selected
            </Button>
          </div>
        )}
      </Card>

      {/* Info banner */}
      <div className="text-xs text-muted-foreground px-1">
        <TrendingUp className="w-3.5 h-3.5 inline mr-1 -mt-0.5 text-emerald-600" />
        Every sale auto-populates this list. <strong>Suggested qty</strong> ={" "}
        max(0, total sold − current stock).
      </div>

      {/* Table */}
      <Card className="overflow-hidden">
        {items.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">
            <Package className="w-12 h-12 mx-auto mb-2 opacity-30" />
            <p className="text-sm">
              {loading ? "Loading…" : "No sale-driven requirements match your filters."}
            </p>
            <p className="text-xs mt-1">
              Record a sale and items will appear here automatically.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/40 text-xs">
                <tr>
                  {canBulkIgnore && statusFilter === "open" && (
                    <th className="p-2 w-10">
                      <Checkbox
                        checked={selectedIds.size > 0 && selectedIds.size === items.filter(r => r.status === "open").length}
                        onCheckedChange={toggleSelectAll}
                        data-testid="sr-select-all"
                      />
                    </th>
                  )}
                  <th className="p-2 text-left">Shop</th>
                  <th className="p-2 text-left">Product</th>
                  <th className="p-2 text-right">Sold</th>
                  <th className="p-2 text-right">Sales</th>
                  <th className="p-2 text-right">On Hand</th>
                  <th className="p-2 text-right">Suggested</th>
                  <th className="p-2 text-left">Last Sale</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <SaleReqRow
                    key={it.id}
                    item={it}
                    onAddToQueue={() => setQueueItem(it)}
                    onIgnore={() => handleIgnore(it.id)}
                    onReopen={() => handleReopen(it.id)}
                    showCheckbox={canBulkIgnore && statusFilter === "open"}
                    isSelected={selectedIds.has(it.id)}
                    onToggleSelect={() => toggleSelect(it.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <AddToQueueDialog
        item={queueItem}
        onCancel={() => setQueueItem(null)}
        onConfirm={(payload) => handleAddToQueue(queueItem.id, payload)}
      />
    </div>
  );
}

function SaleReqRow({ item, onAddToQueue, onIgnore, onReopen, showCheckbox, isSelected, onToggleSelect }) {
  const p = item.product_snapshot || {};
  const lastSale = item.last_sale_at
    ? formatDateTime(item.last_sale_at)
    : "—";
  return (
    <tr className="border-t hover:bg-muted/30" data-testid={`sr-row-${item.id}`}>
      {showCheckbox && (
        <td className="p-2">
          {item.status === "open" && (
            <Checkbox
              checked={isSelected}
              onCheckedChange={onToggleSelect}
              data-testid={`sr-checkbox-${item.id}`}
            />
          )}
        </td>
      )}
      <td className="p-2 font-medium">{item.shop_name}</td>
      <td className="p-2">
        <div className="text-sm font-medium">{p.product_name || "—"}</div>
        <div className="text-[11px] text-muted-foreground">
          {p.brand_name || "—"} · {p.auto_barcode || ""}
        </div>
      </td>
      <td className="p-2 text-right font-mono font-semibold">{item.total_sold_qty}</td>
      <td className="p-2 text-right font-mono text-xs text-muted-foreground">
        {item.sale_count || 0}
      </td>
      <td className="p-2 text-right font-mono">{item.current_stock}</td>
      <td className="p-2 text-right">
        <span
          className={`px-2 py-0.5 rounded text-xs font-mono font-semibold ${
            item.suggested_qty > 0
              ? "bg-amber-100 text-amber-800"
              : "bg-emerald-50 text-emerald-700"
          }`}
        >
          {item.suggested_qty}
        </span>
      </td>
      <td className="p-2 text-xs text-muted-foreground whitespace-nowrap">{lastSale}</td>
      <td className="p-2">
        <span className={`px-2 py-0.5 rounded text-[11px] ${STATUS_STYLE[item.status]}`}>
          {item.status}
        </span>
      </td>
      <td className="p-2 text-right">
        <div className="flex justify-end gap-1">
          {item.status === "open" && (
            <>
              <Button
                size="sm"
                onClick={onAddToQueue}
                className="h-7 text-xs text-white"
                style={{ backgroundColor: "#9E6D5B" }}
                data-testid={`sr-add-to-queue-${item.id}`}
              >
                <Plus className="w-3 h-3 mr-1" />
                Add to Queue
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={onIgnore}
                className="h-7 w-7 p-0 text-muted-foreground hover:text-gray-700"
                title="Ignore"
                data-testid={`sr-ignore-${item.id}`}
              >
                <EyeOff className="w-3.5 h-3.5" />
              </Button>
            </>
          )}
          {item.status === "ignored" && (
            <Button
              size="sm"
              variant="ghost"
              onClick={onReopen}
              className="h-7 text-xs text-muted-foreground hover:text-emerald-700"
              data-testid={`sr-reopen-${item.id}`}
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" />
              Reopen
            </Button>
          )}
          {item.status === "queued" && item.linked_restock_id && (
            <span
              className="inline-flex items-center gap-1 px-2 h-7 text-xs text-blue-700 bg-blue-50 rounded"
              title="Linked restock id"
              data-testid={`sr-queued-${item.id}`}
            >
              <CheckCircle2 className="w-3.5 h-3.5" />
              Queued
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

function AddToQueueDialog({ item, onCancel, onConfirm }) {
  const [qty, setQty] = useState("");
  const [priority, setPriority] = useState("normal");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!item) return;
    // Default qty = suggested (sold-on_hand) or total_sold if zero
    const def = item.suggested_qty > 0 ? item.suggested_qty : item.total_sold_qty;
    setQty(String(def));
    setPriority("normal");
    setNotes(`Auto: sold ${item.total_sold_qty}, on hand ${item.current_stock}`);
  }, [item]);

  if (!item) return null;
  const qtyNum = Number(qty || 0);
  const canSubmit = qtyNum > 0;

  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-md" data-testid="sr-add-dialog">
        <DialogHeader>
          <DialogTitle>Add to Restock Queue</DialogTitle>
          <DialogDescription>
            Convert this sale-driven requirement into a restock-queue row.
          </DialogDescription>
        </DialogHeader>
        <Card className="p-3 bg-slate-50 border-slate-200">
          <div className="text-sm font-medium">
            {item.product_snapshot?.product_name}
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            Shop: <span className="font-medium">{item.shop_name}</span>
          </div>
          <div className="text-xs mt-1">
            Sold <span className="font-mono font-semibold">{item.total_sold_qty}</span> ·{" "}
            On hand <span className="font-mono font-semibold">{item.current_stock}</span> ·{" "}
            Suggested{" "}
            <span className="font-mono font-semibold text-amber-700">
              {item.suggested_qty}
            </span>
          </div>
        </Card>
        <div className="space-y-2">
          <div>
            <Label className="text-xs">Quantity</Label>
            <Input
              type="number"
              min="1"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="h-9 mt-1"
              data-testid="sr-add-qty"
            />
          </div>
          <div>
            <Label className="text-xs">Priority</Label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger className="h-9 mt-1" data-testid="sr-add-priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="normal">Normal</SelectItem>
                <SelectItem value="low">Low</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Notes</Label>
            <Input
              className="h-9 mt-1"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="sr-add-notes"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="sr-add-cancel">
            Cancel
          </Button>
          <Button
            disabled={!canSubmit}
            className="text-white"
            style={{ backgroundColor: "#9E6D5B" }}
            onClick={() => onConfirm({ quantity: qtyNum, priority, notes })}
            data-testid="sr-add-submit"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add to Queue
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
